'use client';

import { c as _c } from "react/compiler-runtime";
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import { PowerBarMarker } from '@/domains/pages/shared/power-bar-marker';
import { getPowerBarLayout, powerBarColoredFillStyle, POWER_BAR_MEDIAN_TICK_COLOR, POWER_BAR_MEDIAN_TICK_COLOR_LIT, POWER_BAR_TRACK_CLASS, powerBarMarkerPosition, selectedValueTone } from '@/domains/pages/shared/power-bar-shared';
import { AIFI_ORG_COLOR, DIMENSION_SCORE_CEILING } from '@/domains/pages/ai-proficiency/constants';
import { cn } from '@/utils/shad';
import { formatScore } from '@/utils/format.util';
import { useCallback, useMemo, useRef, useState } from 'react';
type DimensionPowerBarHoverTarget = 'orgAvg' | 'ceiling';
type DimensionPowerBarProps = {
  score: number;
  orgAvg: number;
  ceiling?: number;
  fillColor: string;
  className?: string;
  compact?: boolean;
};
export function DimensionPowerBar(t0) {
  const $ = _c(67);
  const {
    score,
    orgAvg,
    ceiling: t1,
    fillColor,
    className,
    compact: t2
  } = t0;
  const ceiling = t1 === undefined ? DIMENSION_SCORE_CEILING : t1;
  const compact = t2 === undefined ? false : t2;
  const barRef = useRef(null);
  const [hoverTarget, setHoverTarget] = useState(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      x: 0,
      y: 0
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [tooltipPos, setTooltipPos] = useState(t3);
  let t4;
  if ($[1] !== compact) {
    t4 = getPowerBarLayout(compact);
    $[1] = compact;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const layout = t4;
  let t5;
  if ($[3] !== score) {
    t5 = powerBarMarkerPosition(score);
    $[3] = score;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const scoreFill = t5;
  const vsOrgAvg = score - orgAvg;
  const vsCeiling = score - ceiling;
  const aheadOfOrgAvg = vsOrgAvg >= 0;
  const aheadOfCeiling = vsCeiling >= 0;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = ratio => {
      const bar = barRef.current;
      if (!bar) {
        return;
      }
      const rect = bar.getBoundingClientRect();
      setTooltipPos({
        x: rect.left + rect.width * Math.min(1, Math.max(0, ratio)),
        y: rect.top
      });
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const updateTooltipAtRatio = t6;
  let t7;
  if ($[6] !== orgAvg) {
    t7 = () => {
      updateTooltipAtRatio(orgAvg / 100);
      setHoverTarget("orgAvg");
    };
    $[6] = orgAvg;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const showOrgAvgTooltip = t7;
  let t8;
  if ($[8] !== ceiling) {
    t8 = () => {
      updateTooltipAtRatio(ceiling / 100);
      setHoverTarget("ceiling");
    };
    $[8] = ceiling;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const showCeilingTooltip = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setHoverTarget(null);
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const hideTooltip = t9;
  let t10;
  if ($[11] !== aheadOfCeiling || $[12] !== aheadOfOrgAvg || $[13] !== ceiling || $[14] !== hoverTarget || $[15] !== orgAvg || $[16] !== score || $[17] !== vsCeiling || $[18] !== vsOrgAvg) {
    t10 = hoverTarget === "orgAvg" ? [{
      label: "Org average",
      value: formatScore(orgAvg, {
        decimals: 0
      })
    }, {
      label: "This group",
      value: formatScore(score, {
        decimals: 0
      }),
      valueStyle: {
        color: AIFI_ORG_COLOR
      }
    }, {
      label: "Difference",
      value: `${aheadOfOrgAvg ? "+" : ""}${formatScore(vsOrgAvg, {
        decimals: 0
      })}`,
      ...selectedValueTone(aheadOfOrgAvg)
    }] : hoverTarget === "ceiling" ? [{
      label: "Target",
      value: formatScore(ceiling, {
        decimals: 0
      })
    }, {
      label: "This group",
      value: formatScore(score, {
        decimals: 0
      }),
      valueStyle: {
        color: AIFI_ORG_COLOR
      }
    }, {
      label: "Difference",
      value: `${aheadOfCeiling ? "+" : ""}${formatScore(vsCeiling, {
        decimals: 0
      })}`,
      ...selectedValueTone(aheadOfCeiling)
    }] : [];
    $[11] = aheadOfCeiling;
    $[12] = aheadOfOrgAvg;
    $[13] = ceiling;
    $[14] = hoverTarget;
    $[15] = orgAvg;
    $[16] = score;
    $[17] = vsCeiling;
    $[18] = vsOrgAvg;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const tooltipRows = t10;
  let t11;
  if ($[20] !== className) {
    t11 = cn("relative", className);
    $[20] = className;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const t12 = `${layout.trackHeightPx}px`;
  let t13;
  if ($[22] !== layout.trackTopPx || $[23] !== t12) {
    t13 = {
      top: layout.trackTopPx,
      height: t12
    };
    $[22] = layout.trackTopPx;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className={cn("absolute inset-0", POWER_BAR_TRACK_CLASS)} />;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== fillColor) {
    t15 = powerBarColoredFillStyle(fillColor);
    $[26] = fillColor;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== scoreFill || $[29] !== t15) {
    t16 = <div className="absolute inset-y-0 left-0" style={{
      width: scoreFill,
      ...t15
    }} />;
    $[28] = scoreFill;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t13 || $[32] !== t16) {
    t17 = <div className="absolute right-0 left-0" style={t13}>{t14}{t16}</div>;
    $[31] = t13;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== orgAvg) {
    t18 = powerBarMarkerPosition(orgAvg);
    $[34] = orgAvg;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const t19 = hoverTarget === "orgAvg";
  let t20;
  if ($[36] !== layout || $[37] !== showOrgAvgTooltip || $[38] !== t18 || $[39] !== t19) {
    t20 = <PowerBarMarker layout={layout} position={t18} lit={t19} color={POWER_BAR_MEDIAN_TICK_COLOR} litColor={POWER_BAR_MEDIAN_TICK_COLOR_LIT} ariaLabel="Org average marker" onMouseEnter={showOrgAvgTooltip} onBlur={hideTooltip} />;
    $[36] = layout;
    $[37] = showOrgAvgTooltip;
    $[38] = t18;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== ceiling) {
    t21 = powerBarMarkerPosition(ceiling);
    $[41] = ceiling;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  const t22 = hoverTarget === "ceiling";
  let t23;
  if ($[43] !== fillColor || $[44] !== layout || $[45] !== showCeilingTooltip || $[46] !== t21 || $[47] !== t22) {
    t23 = <PowerBarMarker layout={layout} position={t21} lit={t22} color={fillColor} litColor={fillColor} ariaLabel="Target marker" onMouseEnter={showCeilingTooltip} onBlur={hideTooltip} />;
    $[43] = fillColor;
    $[44] = layout;
    $[45] = showCeilingTooltip;
    $[46] = t21;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== layout.containerHeightPx) {
    t24 = <div style={{
      height: layout.containerHeightPx
    }} />;
    $[49] = layout.containerHeightPx;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== t17 || $[52] !== t20 || $[53] !== t23 || $[54] !== t24) {
    t25 = <div ref={barRef} className="relative" onMouseLeave={hideTooltip}>{t17}{t20}{t23}{t24}</div>;
    $[51] = t17;
    $[52] = t20;
    $[53] = t23;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  const t26 = hoverTarget != null;
  let t27;
  if ($[56] !== tooltipRows) {
    t27 = <ChartTooltipPanel rows={tooltipRows} />;
    $[56] = tooltipRows;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t26 || $[59] !== t27 || $[60] !== tooltipPos.x || $[61] !== tooltipPos.y) {
    t28 = <FloatingTooltip open={t26} x={tooltipPos.x} y={tooltipPos.y}>{t27}</FloatingTooltip>;
    $[58] = t26;
    $[59] = t27;
    $[60] = tooltipPos.x;
    $[61] = tooltipPos.y;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== t11 || $[64] !== t25 || $[65] !== t28) {
    t29 = <div className={t11}>{t25}{t28}</div>;
    $[63] = t11;
    $[64] = t25;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  return t29;
}